import React from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Briefcase, Calendar, Globe, LogOut, ShieldCheck, HardHat } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { isAdmin } from "@/lib/roles";
import { PageHeader, Card } from "@/components/shared/ui";

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 border-b border-slate-100 py-3 last:border-0">
    <Icon className="h-5 w-5 text-slate-400" />
    <div className="flex-1">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-0.5 text-sm text-slate-900">{value}</div>
    </div>
  </div>
);

export default function Settings() {
  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const admin = isAdmin(user);
  const roleDisplay = admin ? t("super_admin") : `${t(user?.role || "")} ${t("engineer")}`;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={t("settings")} subtitle={t("profile")} />

      <Card className="mb-6 flex flex-col items-center py-8 text-center">
        <div className="mb-3 flex h-20 w-20 items-center justify-center rounded-full bg-[var(--brand-light)]">
          <HardHat className="h-10 w-10 text-[var(--brand)]" />
        </div>
        <div className="text-xl font-bold text-slate-900">{user?.name}</div>
        <div className="text-sm text-slate-500">{user?.email}</div>
        <div className="mt-3 flex items-center gap-1.5 rounded-full bg-[var(--brand-light)] px-4 py-1.5 text-sm font-semibold text-[var(--brand)]">
          {admin ? <ShieldCheck className="h-4 w-4" /> : <Briefcase className="h-4 w-4" />}
          {roleDisplay}
        </div>
      </Card>

      <Card className="mb-6">
        <InfoRow icon={Mail} label={t("email")} value={user?.email} />
        <InfoRow icon={Briefcase} label={t("role")} value={roleDisplay} />
        <InfoRow
          icon={Calendar}
          label={t("member_since")}
          value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
        />
      </Card>

      <Card className="mb-6">
        <div className="mb-4 flex items-center gap-2">
          <Globe className="h-5 w-5 text-[var(--brand)]" />
          <span className="text-base font-semibold text-slate-900">{t("language")}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { key: "en", label: t("english") },
            { key: "ku", label: t("kurdish") },
          ].map((l) => (
            <button
              key={l.key}
              data-testid={`lang-${l.key}-btn`}
              onClick={() => setLanguage(l.key)}
              className={`rounded-xl border-2 py-3 text-sm font-semibold transition-colors ${
                language === l.key
                  ? "border-[var(--brand)] bg-[var(--brand-light)] text-[var(--brand)]"
                  : "border-slate-200 bg-slate-50 text-slate-500"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </Card>

      <button
        onClick={handleLogout}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-100 bg-red-50 py-3.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-100"
      >
        <LogOut className="h-5 w-5" /> {t("sign_out")}
      </button>

      <p className="mt-6 text-center text-xs text-slate-400">{t("app_version")}</p>
    </div>
  );
}
